// IMPORTANT NOTE: Please don't import other selectors from this file.
// Use secondOrder and *ThirdOrder for your selector if it has other
// dependencies.

import { createSelector } from 'reselect'

import { desktop } from '../store/defaultSettings'
import { fullSystemStateSelector } from './fullFileFirstOrder'

/**
 * @typedef {import('../index').PlottrState} PlottrState
 * @typedef {import('../index').AppSettings} AppSettings
 * @typedef {import('../index').Settings} Settings
 * @typedef {import('../index').ExportSettings} ExportSettings
 * @typedef {import('../index').UserSettings} UserSettings
 * @typedef {import('../index').GlobalFontsUserAppSettings} GlobalFontsUserAppSettings
 * @typedef {import('../index').TimelineFontsUserAppSettings} TimelineFontsUserAppSettings
 * @typedef {import('../index').RceFontsUserAppSettings} RceFontsUserAppSettings
 * @typedef {import('../types/system').UserAppSettings} UserAppSettings
 * @typedef {import('../types/system').FontsUserAppSettings} FontsUserAppSettings
 * @typedef {import('plottr_locales').Locale} Locale
 */

/**
 * @type {function(PlottrState): Settings}
 */
const settingsSelector = createSelector(fullSystemStateSelector, ({ settings }) => {
  return settings ?? desktop()
})
/**
 * @type {function(PlottrState): (ExportSettings | Null)}
 */
export const exportSettingsSelector = createSelector(
  settingsSelector,
  ({ exportSettings }) => exportSettings
)
// DEPRECATED.  Used to be used to store license info from EDD.
//
// See:
//  - emailAddressSelector, &
//  - userIdSelector.
/**
 * @type {function(PlottrState): (UserSettings | Null)}
 */
export const userSettingsSelector = createSelector(
  settingsSelector,
  ({ userSettings }) => userSettings
)
/**
 * @type {function(PlottrState): (AppSettings | null)}
 */
export const appSettingsSelector = createSelector(settingsSelector, ({ appSettings }) => {
  return appSettings
})

/**
 * @type {function(PlottrState): (UserAppSettings | null)}
 */
export const appUserSettingsSelector = createSelector(appSettingsSelector, (allSettings) => {
  return allSettings?.user || null
})

/**
 * @type {function(PlottrState): Array<String>}
 */
export const recentlyUsedColorsSelector = createSelector(
  appUserSettingsSelector,
  (appUserSettings) => appUserSettings?.recentColors ?? []
)

/**
 * @type {function(PlottrState): FontsUserAppSettings}
 */
export const fontSettingsSelector = createSelector(appUserSettingsSelector, (appUserSettings) => {
  return appUserSettings?.fonts || desktop().user.fonts
})

/**
 * @type {function(PlottrState): (GlobalFontsUserAppSettings | Null)}
 */
export const globalFontsSettingsSelector = createSelector(
  fontSettingsSelector,
  ({ global }) => global ?? null
)

/**
 * @type {function(PlottrState): (TimelineFontsUserAppSettings | Null)}
 */
export const timelineFontsSettingsSelector = createSelector(
  fontSettingsSelector,
  ({ timeline }) => timeline ?? {}
)

/**
 * @type {function(PlottrState): (RceFontsUserAppSettings | null)}
 */
export const rceFontsSettingsSelector = createSelector(fontSettingsSelector, ({ rce }) => rce ?? {})

/**
 * @type {function(PlottrState): boolean}
 */
export const previouslyLoggedIntoProSelector = createSelector(
  appSettingsSelector,
  (appSettings) => {
    return !!appSettings?.user?.choseProMode
  }
)
export const choseProModeSelector = previouslyLoggedIntoProSelector
/**
 * @type {function(PlottrState): boolean}
 */
export const chosePlottrModeSelector = createSelector(appSettingsSelector, (appSettings) => {
  return !!appSettings?.user?.chosePlottrMode
})
/**
 * @type {function(PlottrState): boolean}
 */
export const choseTrialModeSelector = createSelector(appSettingsSelector, (appSettings) => {
  return !!appSettings?.user?.choseTrialMode
})
/**
 * @type {function(PlottrState): (String | Null)}
 */
export const emailFromLastLoginSelector = createSelector(
  appUserSettingsSelector,
  (appUserSettinsg) => {
    return appUserSettinsg?.email || null
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const backupEnabledSelector = createSelector(appSettingsSelector, (appSettings) => {
  return appSettings?.backup ?? true
})
/**
 * @type {function(PlottrState): String}
 */
export const backupFolderPathSelector = createSelector(
  appUserSettingsSelector,
  (appUserSettings) => {
    return appUserSettings?.backupLocation || 'default'
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const showDashboardOnBootSelector = createSelector(
  appUserSettingsSelector,
  (appUserSettings) => appUserSettings?.openDashboardFirst ?? true
)
/**
 * @type {function(PlottrState): boolean}
 */
export const isDarkModeSelector = createSelector(appUserSettingsSelector, (appUserSettings) =>
  appUserSettings ? appUserSettings.dark === 'dark' : false
)
/**
 * @type {function(PlottrState): boolean}
 */
export const localBackupsEnabledSelector = createSelector(
  appUserSettingsSelector,
  (appUserSettings) => appUserSettings?.localBackups ?? true
)
/**
 * @type {function(PlottrState): boolean}
 */
export const useSpellcheckSelector = createSelector(
  appUserSettingsSelector,
  (appUserSettings) => appUserSettings?.useSpellcheck ?? true
)
/**
 * @type {function(PlottrState): boolean}
 */
export const useCardColorAsOutlineSelector = createSelector(
  appUserSettingsSelector,
  (appUserSettings) => appUserSettings?.useCardColorAsOutline ?? true
)
/**
 * @type {function(PlottrState): boolean}
 */
export const trialModeSelector = createSelector(
  appSettingsSelector,
  (appSettings) => appSettings?.trialMode || false
)
/**
 * @type {function(PlottrState): boolean}
 */
export const canGetUpdatesSelector = createSelector(
  appSettingsSelector,
  (appSettings) => appSettings?.canGetUpdates || false
)
/**
 * @type {function(PlottrState): (String | null)}
 */
export const defaultFolderLocationSelector = createSelector(
  appUserSettingsSelector,
  (appUserSettings) => {
    return appUserSettings?.defaultFolderLocation || null
  }
)
/**
 * @type {function(PlottrState): boolean}
 */
export const hasDefaultFolderSelector = createSelector(
  appUserSettingsSelector,
  (appUserSettings) => {
    if (appUserSettings) {
      const { defaultFolder, defaultFolderLocation } = appUserSettings
      return (
        typeof defaultFolder === 'boolean' &&
        defaultFolder &&
        typeof defaultFolderLocation === 'string' &&
        defaultFolderLocation !== ''
      )
    } else {
      return false
    }
  }
)
/**
 * @type {function(PlottrState): Locale}
 */
export const localeSelector = createSelector(appSettingsSelector, (appSettings) => {
  return appSettings?.locale || 'en'
})
/**
 * @type {function(PlottrState): boolean}
 */
export const offlineModeV2EnabledSelector = createSelector(
  appUserSettingsSelector,
  (appUserSettings) => {
    return appUserSettings?.enableOfflineModeV2 || false
  }
)
